/**
 * The scroll tips: a short deck per page, carrying the two or three things
 * worth saying to somebody standing on that page, and where to go next.
 *
 * They exist because the site answers questions in the order *it* finds
 * logical and a buyer arrives with one question of their own — usually about
 * money — which is answered on a page they have not opened. The landed-cost
 * calculator explains that the sticker price is a third of the story, and the
 * only people who read it are the ones who already suspected as much. A tip
 * takes that one sentence to the page the visitor is actually on.
 *
 * ## One deck per page, three slides per deck
 *
 * Until 2026-10-25 three tips covered sixteen routes, grouped by what the
 * visitor was assumed to be doing. That was replaced by one message per page,
 * which fixed the dismissal bug it caused and was better copy anyway. This is
 * the next step and it goes the other way: one page still owns its own
 * messages, but it owns **three of them**, shown in sequence in the one card.
 *
 * A single card had to carry a page's whole argument in about 25 words, and
 * that forced a choice the copy should not have to make — the hook, or the
 * objection, or the thing a buyer would be glad to know. Three slides take all
 * three, and the visitor spends the same amount of attention either way
 * because the card is the same size.
 *
 * ## The shape of a deck
 *
 * Every deck is written in the same order, and the order is the argument:
 *
 * 1. **The hook.** What this page's visitor is most likely wondering, answered
 *    in a sentence, with the page that answers it properly behind the button.
 * 2. **The second angle.** The objection or the next question — usually money,
 *    sometimes trust — pointing somewhere different from slide 1.
 * 3. **The educative slide, which asks for nothing.** One fact worth having,
 *    with no page CTA at all: `cta` is omitted and the card shows the
 *    WhatsApp seat alone. It is the slide that makes the deck a tip rather
 *    than an advert, and every deck has exactly one.
 *
 * Slide 1 is also the slide the rest of the feature borrows: the phone's
 * collapsed pill shows its headline and the launcher disc announces its
 * eyebrow, because those two have one line between them and a deck does not
 * fit in one line. See `components/molecules/scrollTip`.
 *
 * ## What a slide is allowed to be
 *
 * One message, at most one action, short enough to read without stopping. The
 * card holds about 30 words before it stops being a card and starts being a
 * popup, which on a phone is where Google's intrusive-interstitial guidance
 * begins and where a visitor's patience ends. Anything that needs more words
 * than that is a *page*, and the tip's job is to send them to it.
 *
 * Headlines are written to about eight words, which is one line on a phone and
 * the length the collapsed pill can show without clipping. Notification copy
 * research says the same thing in a blunter way: lead with the thing that
 * matters, and cut every word that is not carrying it.
 *
 * **A slide's CTA must never point at the page the deck is on.** Offering a
 * visitor the page they are already reading is the clearest possible signal
 * that nothing here is paying attention. Watch this when adding a slide to a
 * section with children — `/import-guide` carries its articles, so its deck
 * may not point at one of them. Two slides in one deck should also not point
 * at the same page: three buttons to `/quote` is one button and two wasted
 * slides.
 *
 * ## Copy status (§59) — READ BEFORE EDITING
 *
 * The opening slide of `home`, `about` and `import-a-car` is Kabex's own
 * supplied wording. **Everything else here is ours and PENDING KABEX.** It is
 * built the way `content/importPromise.ts` is built — every slide restates a
 * claim the site already makes somewhere a visitor can check:
 *
 *   JEVIC and the auction sheet, the 9–15 year levy, worldwide sourcing,
 *   the destination recorded in the order, inland delivery, progress updates,
 *   the specification confirmed in writing     → `content/site.ts`
 *   30% or 50%, the balance two weeks before port, instalments, e-receipts,
 *   the logbook in your name, invoiced and receipted payments, the go-ahead
 *                                              → `content/paymentTerms.ts`
 *   free, no deposit, nothing reserved, the itemised quotation, advice on a
 *   car you already bought                     → `pages/import-advice/content.ts`
 *   one team from the auction to the registration → the home page services band
 *   the sectors on the client wall             → `content/clients.ts`
 *
 * **Do not add a claim here that no page behind it supports**, and in
 * particular do not reach for a number. `content/stats.ts` says in capitals
 * that its counts are placeholders, and the accessories catalogue says the
 * same of its prices and lead times. A tip is the most interruptive thing on
 * the site, which makes it the worst possible place to be caught overstating.
 */

export type SiteTipCta = {
  label: string;
  /** A site path, and never the page the deck appears on. See above. */
  to: string;
};

export type SiteTipSlide = {
  /** Two or three words above the title, so the card reads as a tip. */
  eyebrow: string;
  /** The hook. One line on a phone, so it has to work at about eight words. */
  title: string;
  /** The substance. Around 25 words; see the note on length above. */
  body: string;
  /**
   * Where the slide sends them, or **omitted** for an educative slide.
   *
   * When it is there it is the whole reason the tip is allowed to interrupt,
   * and it must be a page that answers *this* slide — a message about total
   * cost goes to the page about total cost, not to the quotation form.
   *
   * When it is absent the card shows the WhatsApp seat on its own. That is the
   * one layout decision riding on this field: the action row never disappears,
   * so every slide in a deck is the same height and the card does not resize
   * under the reader as the deck turns.
   */
  cta?: SiteTipCta;
  /**
   * The WhatsApp alternative, written for this slide (§28). Which *number* it
   * opens is the route's business, not the slide's: `WhatsAppButton` resolves
   * the seat for whichever page the card appeared on.
   */
  whatsappMessage: string;
};

/**
 * A page's deck. **At least three slides**, and the type says so rather than
 * trusting a comment: a two-slide deck is a carousel with nothing to carousel,
 * and the controls would be chrome around a single message.
 */
export type SiteTip = {
  slides: readonly [SiteTipSlide, SiteTipSlide, SiteTipSlide, ...SiteTipSlide[]];
};

const tips = {
  /**
   * The home page. Slide 1 is KABEX SUPPLIED — "watch this!" became "know
   * this" (there is no video on this site to watch), the supplied typos are
   * corrected, and the closing belief line is dropped because
   * /free-import-advice already argues it in words it has approval for ("Ask
   * before you buy, not after it lands").
   *
   * Slides 2 and 3 are the two things a first-time visitor most often does not
   * know and would not think to ask: that an import does not need the whole
   * price up front, and whose name ends up on the logbook.
   */
  home: {
    slides: [
      {
        eyebrow: 'Before you buy',
        title: 'Before you import a car, know this.',
        body: 'The price matters — but so do mileage, condition, shipping, taxes and the total cost of getting your car to Uganda.',
        cta: { label: 'Get free advice', to: '/free-import-advice' },
        whatsappMessage:
          'Hello Kabex Motors, I would like free advice on what importing a car would really cost me.',
      },
      {
        eyebrow: 'Start smaller',
        title: 'You do not need the full amount to begin.',
        body: 'An import opens at 30% or 50% of the CIF price. The balance is due two weeks before your car reaches port, in instalments if you prefer.',
        cta: { label: 'See how it works', to: '/how-it-works' },
        whatsappMessage: 'Hello Kabex Motors, what would it take to start my import today?',
      },
      {
        eyebrow: 'Worth knowing',
        title: 'The logbook comes in your name.',
        body: 'Not a dealership’s name and not ours. Your car is registered to you the first time it is registered at all, so there is no transfer to pay for.',
        whatsappMessage:
          'Hello Kabex Motors, can you explain how registration works on an imported car?',
      },
    ],
  },

  /**
   * About. Slide 1 is KABEX SUPPLIED, trimmed from ~50 words: the closing
   * exhortation ("Why wait? Your next car journey starts today") is what the
   * button already is, and "from Japan to East Africa" went with the clause it
   * sat in, because the rest of the site says Kabex sources worldwide and the
   * narrower line could not be kept as supplied without contradicting it.
   *
   * A page about the company is the right place for the two claims that are
   * about *how* it works rather than what it sells.
   */
  about: {
    slides: [
      {
        eyebrow: 'Our commitment',
        title: 'Your dream car. Our commitment.',
        body: 'From everyday cars to luxury SUVs, we guide you through sourcing, shipping, taxes, clearance and delivery — with transparency at every step.',
        cta: { label: 'Get a quote', to: '/quote' },
        whatsappMessage:
          'Hello Kabex Motors, I would like to start importing my preferred vehicle.',
      },
      {
        eyebrow: 'One team',
        title: 'From the auction to the logbook.',
        body: 'The people who source your car are the people who see it through shipping, taxes, clearing and registration — not an agent you have never met.',
        cta: { label: 'See how it works', to: '/how-it-works' },
        whatsappMessage: 'Hello Kabex Motors, who would handle my import from start to finish?',
      },
      {
        eyebrow: 'In writing first',
        title: 'The car that lands is the car you chose.',
        body: 'Make, model, year, mileage and trim are confirmed in writing before any payment, and nothing begins until you give a clear go-ahead.',
        whatsappMessage:
          'Hello Kabex Motors, how do you confirm the specification of a car before I pay?',
      },
    ],
  },

  /**
   * Import a car. Slide 1 is KABEX SUPPLIED, trimmed from ~45 words on the
   * same basis as `about`. The deck then answers the two questions a buyer on
   * this page asks next, in order: where does the car come from, and what
   * exactly am I being charged for.
   */
  'import-a-car': {
    slides: [
      {
        eyebrow: 'Any budget',
        title: 'Whatever your budget, let’s find your car.',
        body: 'From affordable family cars to luxury SUVs, we’ll find a vehicle that matches what you need and what you can spend.',
        cta: { label: 'See all cars', to: '/cars' },
        whatsappMessage:
          'Hello Kabex Motors, I have a budget in mind — can you help me find a car to match it?',
      },
      {
        eyebrow: 'Sourced to order',
        title: 'We buy the car you chose.',
        body: 'Tell us the make, model, year and budget, and Kabex sources it from markets around the world — not from whatever happens to be standing in a yard.',
        cta: { label: 'Get a quote', to: '/quote' },
        whatsappMessage:
          'Hello Kabex Motors, I know the make, model and year I want — can you source it?',
      },
      {
        eyebrow: 'What you pay for',
        title: 'Your quotation itemises every cost.',
        body: 'The car, shipping, URA taxes and clearing, each as its own line — so nothing is bundled into a single figure you cannot take apart.',
        whatsappMessage:
          'Hello Kabex Motors, could you show me how an import quotation is broken down?',
      },
    ],
  },

  /**
   * The listings. Somebody comparing prices is comparing the one number that
   * is not the price they will pay, so this is the page to say so — and to
   * hand them the tool rather than the argument. Slide 2 narrows it to the
   * single variable most likely to change a shortlist, and slide 3 answers the
   * question a list of cars cannot: whether any of them are sound.
   *
   * Slide 1's CTA names the calculator the way the site names it: "Drive-Home
   * Cost" everywhere a visitor sees it, with the URL keeping "landed cost" for
   * search (IA §7, A4b).
   */
  cars: {
    slides: [
      {
        eyebrow: 'Comparing prices',
        title: 'The sticker price is the start, not the total.',
        body: 'Shipping, duty, VAT and clearing all land on top of it. Put a car through the calculator before you shortlist it.',
        cta: { label: 'Work out the drive-home cost', to: '/landed-cost-calculator' },
        whatsappMessage:
          'Hello Kabex Motors, I am comparing a few cars — can you help me work out what each would really cost?',
      },
      {
        eyebrow: 'Mind the year',
        title: 'Two similar cars, two different tax bills.',
        body: 'Passenger vehicles aged 9–15 years are listed with a 50% environmental levy, so the year on your shortlist moves the total as much as the price does.',
        cta: { label: 'Check the tax', to: '/tax-calculator' },
        whatsappMessage:
          'Hello Kabex Motors, can you check the tax on a car of the year I am looking at?',
      },
      {
        eyebrow: 'Before you commit',
        title: 'Every car we ship is inspected first.',
        body: 'JEVIC inspects it before it sails, and our team walks you through the available auction sheet and vehicle details before you pay anything.',
        whatsappMessage:
          'Hello Kabex Motors, could you send me the inspection details for a car I am looking at?',
      },
    ],
  },

  /**
   * A single vehicle, which is the moment the question stops being "what does
   * importing cost" and becomes "is *this* car sound". The answer the site
   * already has for that is the inspection and the auction sheet — then the
   * cost of this particular car, then the one reassurance that outlasts the
   * purchase.
   */
  vehicle: {
    slides: [
      {
        eyebrow: 'Before you commit',
        title: 'Ask to see the auction sheet.',
        body: 'Every car we ship is inspected by JEVIC before it sails, and our team will walk you through the sheet before you pay anything.',
        cta: { label: 'Ask about this car', to: '/free-import-advice' },
        whatsappMessage:
          'Hello Kabex Motors, could you send me the auction sheet and details for a car I am looking at?',
      },
      {
        eyebrow: 'On the road',
        title: 'Ask what this one costs delivered.',
        body: 'Shipping, duty, VAT and clearing land on top of the price shown here. The calculator gives you the drive-home figure in a minute.',
        cta: { label: 'Work out the drive-home cost', to: '/landed-cost-calculator' },
        whatsappMessage:
          'Hello Kabex Motors, what would this car cost me delivered and registered?',
      },
      {
        eyebrow: 'Owned outright',
        title: 'Registered to you, first time round.',
        body: 'No dealership on the logbook, no previous owner, and no transfer fee for an ownership change that should never have happened.',
        whatsappMessage:
          'Hello Kabex Motors, how does registration work on a car you import for me?',
      },
    ],
  },

  /**
   * The tax calculator. The levy is the single fact most likely to change
   * somebody's shortlist, and it turns on the year — which is exactly the
   * field they are typing into when this appears. Slide 2 is the handover to
   * the real figure, and slide 3 says plainly that the rules are checked for
   * each car rather than read off a table once.
   */
  'tax-calculator': {
    slides: [
      {
        eyebrow: 'Mind the year',
        title: 'Nine years old is a different tax bill.',
        body: 'Passenger vehicles aged 9–15 years are listed with a 50% environmental levy. We check the current treatment for your car.',
        cta: { label: 'Get free advice', to: '/free-import-advice' },
        whatsappMessage:
          'Hello Kabex Motors, can you check the current tax treatment for the car I am considering?',
      },
      {
        eyebrow: 'From estimate to figure',
        title: 'The number you can act on comes in writing.',
        body: 'Your quotation sets out the car, shipping, URA taxes and clearing as separate lines, and states the CIF destination it is priced to.',
        cta: { label: 'Get a quote', to: '/quote' },
        whatsappMessage:
          'Hello Kabex Motors, I have used the tax calculator and would like a formal quotation.',
      },
      {
        eyebrow: 'Why we check',
        title: 'URA rules move. Your car is assessed once.',
        body: 'Before you decide, we go through the model, the year and what the age limit and the levy mean for the car in front of you.',
        whatsappMessage:
          'Hello Kabex Motors, can you take me through the current URA rules for my car?',
      },
    ],
  },

  /**
   * The drive-home cost calculator. Its own page already says "estimates
   * only"; what it cannot say in a lede is what replaces the estimate, which
   * is the written quotation and what that quotation is obliged to name. Slide
   * 2 is the variable the calculator cannot know — where the car stops — and
   * slide 3 is the one every visitor doing sums on this page wants: that the
   * total does not have to be found in one piece.
   */
  'landed-cost-calculator': {
    slides: [
      {
        eyebrow: 'Estimate only',
        title: 'This is a guide, not your invoice.',
        body: 'Your written quotation names the CIF destination and the services included — so you know what is in the number before you commit.',
        cta: { label: 'Get a quote', to: '/quote' },
        whatsappMessage:
          'Hello Kabex Motors, I have used the calculator and would like a formal quotation.',
      },
      {
        eyebrow: 'Where it stops',
        title: 'The total depends on the handover point.',
        body: 'Your order records where Kabex hands the vehicle over and which services are included, so the figure covers a journey you both agreed.',
        cta: { label: 'See handover points', to: '/destinations' },
        whatsappMessage: 'Hello Kabex Motors, where could you hand over a car for me?',
      },
      {
        eyebrow: 'Paying it down',
        title: 'The total does not arrive all at once.',
        body: 'An import opens at 30% or 50% of CIF, and the balance can be paid in instalments across the weeks your car is at sea.',
        whatsappMessage: 'Hello Kabex Motors, how would the payments on an import be spread out?',
      },
    ],
  },

  /**
   * The services hub, and every service page under it. The objection here is
   * the one every importer meets: how many different people am I going to be
   * handed between. Then the free first step, then what being a client
   * actually feels like week to week.
   */
  services: {
    slides: [
      {
        eyebrow: 'One team',
        title: 'Sourcing to number plates, handled in-house.',
        body: 'You deal with one team from the auction to the logbook, not a clearing agent you have never met.',
        cta: { label: 'See how it works', to: '/how-it-works' },
        whatsappMessage: 'Hello Kabex Motors, which services would my import need?',
      },
      {
        eyebrow: 'Advice first',
        title: 'The first conversation is free.',
        body: 'Free guidance on the model, the year and current URA rules before you decide. No deposit, and nothing is reserved.',
        cta: { label: 'Get free advice', to: '/free-import-advice' },
        whatsappMessage: 'Hello Kabex Motors, I would like free advice before I choose a car.',
      },
      {
        eyebrow: 'Kept informed',
        title: 'You hear from us at every stage.',
        body: 'A free consultation, then a formal quotation, then progress updates as your import moves through the process you agreed.',
        whatsappMessage: 'Hello Kabex Motors, how would you keep me updated during my import?',
      },
    ],
  },

  /**
   * The process page. Somebody who has read how it works has one question
   * left, and it is what it takes to begin — so this is the page that may
   * state the money outright. Slide 2 gives the cheaper first step for
   * everyone not ready for that, and slide 3 is the trust claim that belongs
   * next to any mention of paying.
   */
  'how-it-works': {
    slides: [
      {
        eyebrow: 'What it takes to start',
        title: '30% or 50% of CIF starts your import.',
        body: 'The balance is due two weeks before the car reaches port, and it can be paid in instalments rather than one transfer.',
        cta: { label: 'Get a quote', to: '/quote' },
        whatsappMessage: 'Hello Kabex Motors, what would it take to start my import?',
      },
      {
        eyebrow: 'Nothing to lose',
        title: 'Ask before you buy, not after it lands.',
        body: 'Free advice on the model, the year and what the car will really cost to land. No deposit, and nothing is reserved.',
        cta: { label: 'Get free advice', to: '/free-import-advice' },
        whatsappMessage: 'Hello Kabex Motors, I have a question before I commit to a car.',
      },
      {
        eyebrow: 'Every shilling',
        title: 'Invoiced, receipted, and never through this site.',
        body: 'Payments go to the Kabex Motors company account against an invoice, and an e-receipt follows each one. Nothing is payable through this website.',
        whatsappMessage: 'Hello Kabex Motors, how are payments made and receipted on an import?',
      },
    ],
  },

  /**
   * The client roster. The page proves the company is trusted by
   * organisations; the deck's job is to tell an organisation that the same
   * door is open to them, at any size, through the same process a single buyer
   * uses.
   */
  clients: {
    slides: [
      {
        eyebrow: 'Buying for an organisation',
        title: 'Ministries, banks and universities import through us.',
        body: 'One executive car or a whole fleet, sourced and cleared the same way, with the destination and services agreed in writing first.',
        /* "Get a fleet quote" and not `fleetCta`'s own "Get a fleet quotation",
           which is the canonical label elsewhere: at 360px the longer one pushes
           the WhatsApp button onto a second row and the card grows a line taller
           than every other slide. It is the only CTA on the site that does not
           fit beside it. */
        cta: { label: 'Get a fleet quote', to: '/quote' },
        whatsappMessage: 'Hello Kabex Motors, I am buying vehicles for an organisation.',
      },
      {
        eyebrow: 'Any size of order',
        title: 'One executive car, or the whole fleet.',
        body: 'Each vehicle is specified, sourced and cleared the same way, and your order records the handover point and the services included.',
        cta: { label: 'Talk to our team', to: '/contact' },
        whatsappMessage:
          'Hello Kabex Motors, I would like to discuss a fleet order for my organisation.',
      },
      {
        eyebrow: 'Across sectors',
        title: 'From ministries to hospitals and banks.',
        body: 'The organisations here sit in government, banking, education, healthcare and logistics — and every one imported through the process a single buyer uses.',
        whatsappMessage:
          'Hello Kabex Motors, have you supplied vehicles to organisations in my sector?',
      },
    ],
  },

  /**
   * Reviews. The page is other people's endings; the deck points at how each
   * of them began, how little it took, and the one thing that happens before
   * any money moves.
   */
  reviews: {
    slides: [
      {
        eyebrow: 'Your turn',
        title: 'Every one of these started with a question.',
        body: 'Ask yours free: the model, the year, and what it will really cost to land. No deposit, nothing reserved.',
        cta: { label: 'Get free advice', to: '/free-import-advice' },
        whatsappMessage: 'Hello Kabex Motors, I have a question before I decide on a car.',
      },
      {
        eyebrow: 'What they began with',
        title: 'A make, a model, a year and a budget.',
        body: 'That is the whole of it. We source the vehicle to order and hand it over at the destination recorded in your order.',
        cta: { label: 'Start my import', to: '/import-a-car' },
        whatsappMessage: 'Hello Kabex Motors, I know roughly what I want — how do I get started?',
      },
      {
        eyebrow: 'Before anything moves',
        title: 'Nothing begins without your go-ahead.',
        body: 'Kabex starts only after you have reviewed the quotation and said yes to it, with the specification confirmed in writing first.',
        whatsappMessage: 'Hello Kabex Motors, what exactly do I agree to before an import starts?',
      },
    ],
  },

  /**
   * Destinations. The page lists where cars can be handed over; the deck says
   * that the choice is written down rather than settled at the port, that the
   * journey does not stop at the water, and that none of it is improvised.
   */
  destinations: {
    slides: [
      {
        eyebrow: 'Where it ends up',
        title: 'Your order names the handover point.',
        body: 'The destination and the services included are agreed in writing before the order is confirmed, so nobody improvises at the port.',
        cta: { label: 'Get a quote', to: '/quote' },
        whatsappMessage: 'Hello Kabex Motors, where can you hand over a car for me?',
      },
      {
        eyebrow: 'Past the port',
        title: 'Delivery does not stop at the water.',
        body: 'Kabex coordinates inland delivery to the destination in your order, working with carriage partners it already has relationships with.',
        cta: { label: 'See our services', to: '/services' },
        whatsappMessage: 'Hello Kabex Motors, can you deliver a car inland to my town?',
      },
      {
        eyebrow: 'Agreed in advance',
        title: 'The services are listed before you commit.',
        body: 'Your quotation records which of them are in your package, so what happens after the ship docks is a document rather than a conversation.',
        whatsappMessage:
          'Hello Kabex Motors, which services would be included in getting a car to me?',
      },
    ],
  },

  /**
   * The delivery archive. Deliberately about importing rather than about the
   * photographs: the media on that page is still placeholder (A17-style, see
   * `config/deliveryMedia.ts`), so a slide praising the pictures would be a
   * claim about sample data. What the deck may say is what every one of those
   * cars went through on the way here.
   */
  deliveries: {
    slides: [
      {
        eyebrow: 'Yours next',
        title: 'Start the one that ends like this.',
        body: 'Tell us the make, model, year and budget. We source the vehicle to order and hand it over where you agreed.',
        cta: { label: 'Start my import', to: '/import-a-car' },
        whatsappMessage: 'Hello Kabex Motors, I would like to start importing a car.',
      },
      {
        eyebrow: 'Checked before it sailed',
        title: 'Each one was inspected before shipping.',
        body: 'JEVIC inspects every vehicle Kabex ships, and our team walks you through the available auction sheet before you pay anything.',
        cta: { label: 'Ask about a car', to: '/free-import-advice' },
        whatsappMessage: 'Hello Kabex Motors, how do you check a car before it is shipped?',
      },
      {
        eyebrow: 'Handed over owned',
        title: 'Each one was registered to its owner.',
        body: 'No dealership on the logbook and no previous owner — these cars were registered to the people standing next to them, first time round.',
        whatsappMessage: 'Hello Kabex Motors, will the car be registered in my name?',
      },
    ],
  },

  /**
   * The guide index, and every article under it. Slide 1's CTA deliberately
   * leaves the guide altogether rather than pointing at one of its own
   * articles — this deck also shows on those articles, so an article link
   * would sooner or later be a card offering the page it is sitting on. The
   * same applies to slides 2 and 3.
   */
  'import-guide': {
    slides: [
      {
        eyebrow: 'Worth knowing',
        title: 'Five mistakes we see again and again.',
        body: 'Every one of them is avoidable, and every one costs more to undo than it would have cost to ask about first.',
        cta: { label: 'Ask before you buy', to: '/free-import-advice' },
        whatsappMessage:
          'Hello Kabex Motors, I have been reading the import guide and have a question.',
      },
      {
        eyebrow: 'The year first',
        title: 'Age changes the tax, not just the price.',
        body: 'Passenger vehicles aged 9–15 years are listed with a 50% environmental levy. Check the year before you fall for the car.',
        cta: { label: 'Check the tax', to: '/tax-calculator' },
        whatsappMessage:
          'Hello Kabex Motors, how much does the year of a car change what I would pay?',
      },
      {
        eyebrow: 'Read this twice',
        title: 'The cheapest car is rarely the cheapest import.',
        body: 'Shipping, URA taxes and clearing land on top of the price, and none of them shrink just because the car at the auction was cheap.',
        whatsappMessage: 'Hello Kabex Motors, can you help me compare the full cost of two cars?',
      },
    ],
  },

  /**
   * Accessories. The catalogue is sample data until Kabex supplies a real one
   * (A17), so every slide here promises an answer rather than a part, a price
   * or a lead time — none of which the page is currently entitled to claim.
   */
  accessories: {
    slides: [
      {
        eyebrow: 'Fitting it out',
        title: 'Ask us what fits your car.',
        body: 'Tell us the make, model and year, and we will tell you what we can source for it.',
        cta: { label: 'Talk to our team', to: '/contact' },
        whatsappMessage: 'Hello Kabex Motors, what accessories can you source for my car?',
      },
      {
        eyebrow: 'Ask early',
        title: 'Best asked while the car is still at sea.',
        body: 'Tell us what you want fitted before the car lands, and we will tell you what can be sourced to meet it here.',
        cta: { label: 'Ask our team', to: '/free-import-advice' },
        whatsappMessage:
          'Hello Kabex Motors, my car is on its way — can you source accessories to meet it?',
      },
      {
        eyebrow: 'Quoted like the rest',
        title: 'Parts are priced in writing too.',
        body: 'What we can source, and what it would cost, set out in writing before you commit to any of it.',
        whatsappMessage: 'Hello Kabex Motors, could you quote me for accessories for my vehicle?',
      },
    ],
  },

  /**
   * Contact. Somebody on this page is already reaching out, so the deck does
   * not ask them to — it removes the last reason to hesitate, which is the
   * suspicion that asking costs something, then offers the shortcut for
   * visitors who are further along than the form assumes.
   */
  contact: {
    slides: [
      {
        eyebrow: 'Before you call',
        title: 'Asking commits you to nothing.',
        body: 'Tell us what you are considering and someone who imports for a living will tell you what we would do.',
        cta: { label: 'Get free advice', to: '/free-import-advice' },
        whatsappMessage: 'Hello Kabex Motors, I would like some advice before I decide.',
      },
      {
        eyebrow: 'Already know the car?',
        title: 'Skip the questions, ask for the quotation.',
        body: 'The car, shipping, URA taxes and clearing, each as its own line. You can always come back for advice afterwards.',
        cta: { label: 'Get a quote', to: '/quote' },
        whatsappMessage:
          'Hello Kabex Motors, I know the car I want and would like a quotation for it.',
      },
      {
        eyebrow: 'What happens next',
        title: 'A free consultation, then a written quotation.',
        body: 'After that, progress updates as your import moves through the agreed process. No deposit to talk to us, and nothing reserved.',
        whatsappMessage: 'Hello Kabex Motors, what happens after I get in touch?',
      },
    ],
  },
} as const satisfies Record<string, SiteTip>;

/** One of the decks above. Referenced by `config/pageTips.ts`. */
export type SiteTipId = keyof typeof tips;

export const siteTips: Readonly<Record<SiteTipId, SiteTip>> = tips;

/**
 * The deck's own chrome, which is the same whichever page it is on.
 *
 * "Hide this tip" rather than "Close": a visitor deciding whether to press it
 * is deciding whether to make the thing go away, and "close" does not say
 * whether it comes back. See `hooks/useScrollNudge.ts` for the answer — the
 * card collapses into the launcher, which stays put for the rest of the
 * session, so "hide" is now literally true rather than nearly true.
 *
 * `launcher` is the launcher disc's spoken name, and it is prefixed to the
 * first slide's eyebrow rather than standing alone: a screen reader user hears
 * "Show tip: Before you buy" and knows what they would be opening, where
 * "Show tip" on its own is the same unhelpful label on all sixteen pages.
 *
 * The carousel labels follow the WAI-ARIA carousel pattern: the rotating
 * region is named, each slide announces its position, the pause control is a
 * real button whose name says what pressing it does, and the dots say where
 * they would take you rather than merely which one is current.
 */
export const tipChrome = {
  dismiss: 'Hide this tip',
  launcher: 'Show tip',
  /** `aria-label` on the region that carries `aria-roledescription="carousel"`. */
  carousel: 'Tips for this page',
  pause: 'Stop the tips changing',
  play: 'Let the tips change again',
  /**
   * A slide's own label — "Start smaller, 2 of 3".
   *
   * The eyebrow and then the position, which is the format
   * `IntroSlideshow` already uses on the home hero ("Toyota, 1 of 5"). Two
   * carousels on one page announcing their slides two different ways is the
   * kind of inconsistency only a screen reader user ever meets, which is
   * exactly why it is worth matching. The position on its own would also be
   * the one label that tells a visitor nothing about what they are on.
   */
  slidePosition: (eyebrow: string, index: number, total: number) =>
    `${eyebrow}, ${index + 1} of ${total}`,
  goToSlide: (index: number, total: number) => `Show tip ${index + 1} of ${total}`,
} as const;
